/* Wallboard — Fullscreen TV-style NOC dashboard with AMI live push.
 *
 * Auto-refresh every 3s + sub-second push from /ws/active-calls
 * (AMI events: DialBegin / Hangup / Bridge* trigger immediate refetch).
 *
 * Layout (4-column grid):
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ HEADER  · live time · AMI ribbon · fullscreen toggle        │
 *   ├──────────┬──────────┬───────────┬────────────────────────────┤
 *   │ ACTIVE   │ TODAY    │ ANSWERED  │ MISSED                    │  Top KPI tiles
 *   ├──────────┴──────────┼───────────┴────────────────────────────┤
 *   │ EXT online/total    │ ANSWER RATE bar                       │  Mid row
 *   ├─────────────────────┴───────────────────────────────────────┤
 *   │ HOUR DISTRIBUTION (sparkline 24h)                            │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ ACTIVE CALLS TABLE                                           │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │ RECENT │ QUEUES │ EXTENSIONS LIVE                            │
 *   └─────────────────────────────────────────────────────────────┘
 */
import React, { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import {
    Phone, PhoneSlash, CheckCircle, Users, Queue, Broadcast,
    ArrowsOut, X, ChartLineUp, Lightning,
} from '@phosphor-icons/react';

// ---------- Atomic UI pieces ----------------------------------------------
const Tile = ({ label, value, sub, color = '#22c55e', icon: Icon, tid, big = false }) => (
    <div data-testid={tid}
         className="border p-6 flex flex-col justify-center transition-colors"
         style={{
             background: 'rgba(255,255,255,0.02)',
             borderColor: `${color}44`,
             borderRadius: 2,
             boxShadow: `inset 0 0 24px ${color}11`,
         }}>
        <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em]"
                  style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</span>
            {Icon && <Icon size={big ? 32 : 22} weight="duotone" style={{ color }} />}
        </div>
        <div className={`font-bold font-mono leading-none tabular-nums ${big ? 'text-[80px]' : 'text-[56px]'}`}
             style={{ color }}>
            {value}
        </div>
        {sub && <div className="text-xs mt-2 font-mono" style={{ color: 'rgba(255,255,255,0.55)' }}>{sub}</div>}
    </div>
);

// ---- 24h bar sparkline ---------------------------------------------------
const HourChart = ({ data }) => {
    const max = Math.max(1, ...data.map((d) => d.count));
    const nowHour = new Date().getHours();
    return (
        <div data-testid="wb-hour-chart"
             className="border p-5"
             style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(34,197,94,0.25)', borderRadius: 2 }}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold"
                     style={{ color: '#22c55e' }}>
                    <ChartLineUp size={12} className="inline mr-1.5" weight="bold" />
                    Distribuce dnes (24h)
                </div>
                <div className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    peak {max} hovorů/h
                </div>
            </div>
            <div className="flex items-end gap-1 h-24">
                {data.map((d) => {
                    const isNow = d.hour === nowHour;
                    const h = max ? Math.max(2, (d.count / max) * 92) : 2;
                    return (
                        <div key={d.hour}
                             className="flex-1 flex flex-col items-center justify-end gap-1 relative group">
                            {d.count > 0 && (
                                <span className="text-[9px] font-mono"
                                      style={{ color: isNow ? '#22c55e' : 'rgba(255,255,255,0.55)' }}>
                                    {d.count}
                                </span>
                            )}
                            <div className="w-full transition-all"
                                 style={{
                                     height: `${h}px`,
                                     background: isNow
                                         ? 'linear-gradient(to top, #22c55e, #16a34a)'
                                         : 'linear-gradient(to top, rgba(34,197,94,0.35), rgba(34,197,94,0.15))',
                                     borderRadius: 1,
                                     boxShadow: isNow ? '0 0 10px #22c55e88' : 'none',
                                 }} />
                            <span className="text-[8px] font-mono"
                                  style={{ color: isNow ? '#22c55e' : 'rgba(255,255,255,0.4)' }}>
                                {String(d.hour).padStart(2, '0')}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ---- Answer-rate horizontal bar -----------------------------------------
const AnswerRateBar = ({ rate, answered, total }) => {
    const color = rate >= 90 ? '#22c55e' : rate >= 70 ? '#f59e0b' : '#ef4444';
    return (
        <div data-testid="wb-rate-bar"
             className="border p-6 flex flex-col justify-center"
             style={{ background: 'rgba(255,255,255,0.02)', borderColor: `${color}44`, borderRadius: 2 }}>
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold"
                      style={{ color: 'rgba(255,255,255,0.6)' }}>Answer rate</span>
                <CheckCircle size={22} weight="duotone" style={{ color }} />
            </div>
            <div className="text-[56px] font-bold font-mono leading-none tabular-nums" style={{ color }}>
                {rate.toFixed(1)}<span className="text-3xl">%</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden"
                 style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 1 }}>
                <div style={{
                    width: `${Math.min(100, rate)}%`,
                    height: '100%',
                    background: color,
                    transition: 'width 600ms ease',
                    boxShadow: `0 0 12px ${color}aa`,
                }} />
            </div>
            <div className="text-xs mt-2 font-mono" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {answered} z {total} hovorů dnes
            </div>
        </div>
    );
};

// ---- AMI Live ribbon (top of page) ---------------------------------------
const AmiRibbon = ({ amiStatus, lastEvent }) => {
    const ok = amiStatus?.connected;
    const color = ok ? '#22c55e' : '#ef4444';
    return (
        <div data-testid="wb-ami-ribbon"
             className="border px-4 py-2 flex items-center gap-3 text-xs"
             style={{
                 background: `${color}10`,
                 borderColor: `${color}55`,
                 borderRadius: 2,
                 boxShadow: `inset 0 0 16px ${color}11`,
             }}>
            <Broadcast size={14} weight="duotone" style={{ color }} />
            <span className="font-bold uppercase tracking-[0.15em]" style={{ color }}>
                AMI {ok ? 'LIVE' : 'OFFLINE'}
            </span>
            {amiStatus && (
                <span className="font-mono" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    · {amiStatus.events_received ?? 0} events
                    · last: <span style={{ color: '#e2e8f0' }}>{amiStatus.last_event_name || '—'}</span>
                </span>
            )}
            {lastEvent && (
                <span className="font-mono ml-auto animate-pulse"
                      style={{ color: '#22c55e' }}>
                    <Lightning size={11} weight="fill" className="inline mr-1" />
                    {lastEvent.name} · {lastEvent.caller || lastEvent.peer || '—'}
                </span>
            )}
        </div>
    );
};

// ---------- Main page -----------------------------------------------------
const Wallboard = () => {
    const [stats, setStats] = useState(null);
    const [amiStatus, setAmiStatus] = useState(null);
    const [lastAmiEvent, setLastAmiEvent] = useState(null);
    const [fs, setFs] = useState(false);
    const wsRef = useRef(null);
    const lastEventTimer = useRef(null);

    // Polling fetch (3 s base cadence)
    useEffect(() => {
        const load = async () => {
            try {
                const [r1, r2] = await Promise.all([
                    api.get('/wallboard/stats'),
                    api.get('/ami/status').catch(() => ({ data: null })),
                ]);
                setStats(r1.data);
                if (r2.data) setAmiStatus(r2.data);
            } catch (_) { /* ignore */ }
        };
        load();
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, []);

    // AMI WS push — instant refresh on DialBegin / Hangup / Bridge*
    useEffect(() => {
        const token = localStorage.getItem('vg_token');
        if (!token) return undefined;
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const proto = backend.startsWith('https') ? 'wss' : 'ws';
        const host = backend.replace(/^https?:\/\//, '');
        const url = `${proto}://${host}/api/ws/active-calls?token=${encodeURIComponent(token)}`;
        let ws;
        try {
            ws = new WebSocket(url);
            wsRef.current = ws;
            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    if (msg.type === 'ami-event') {
                        setLastAmiEvent({
                            name: msg.event,
                            caller: msg.caller_id_num,
                            peer: msg.peer,
                            ts: msg.ts,
                        });
                        // Auto-fade the flash after 4 s
                        if (lastEventTimer.current) clearTimeout(lastEventTimer.current);
                        lastEventTimer.current = setTimeout(() => setLastAmiEvent(null), 4000);
                        if (['DialBegin', 'DialEnd', 'Hangup', 'BridgeEnter',
                             'BridgeLeave', 'Newchannel'].includes(msg.event)) {
                            api.get('/wallboard/stats').then((r) => setStats(r.data)).catch(() => {});
                        }
                    }
                } catch (_) { /* ignore */ }
            };
        } catch (_) { /* ignore */ }
        return () => { try { ws?.close(); } catch (_) { /* noop */ } };
    }, []);

    const enterFs = async () => {
        try { await document.documentElement.requestFullscreen(); setFs(true); }
        catch (_) { /* noop */ }
    };
    const exitFs = async () => {
        try { await document.exitFullscreen(); } catch (_) { /* noop */ }
        setFs(false);
    };
    useEffect(() => {
        const h = () => setFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', h);
        return () => document.removeEventListener('fullscreenchange', h);
    }, []);

    if (!stats) {
        return <div className="text-center py-20" data-testid="wallboard-loading"
                    style={{ color: '#22c55e' }}>Načítám live wallboard…</div>;
    }
    const t = stats.today;
    const peers = amiStatus?.peer_status || {};
    const peerList = Object.entries(peers).slice(0, 16);

    return (
        <div data-testid="wallboard-page"
             className={fs ? 'fixed inset-0 z-50 overflow-auto p-6' : ''}
             style={fs ? { background: '#0B0E14' } : {}}>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.4em] font-bold"
                         style={{ color: '#22c55e' }}>Solar Green VOIP</div>
                    <h1 className="text-5xl font-heading font-bold tracking-tight">WALLBOARD</h1>
                    <div className="text-xs mt-1 font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {new Date(stats.timestamp).toLocaleString('cs-CZ')}
                    </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                    <AmiRibbon amiStatus={amiStatus} lastEvent={lastAmiEvent} />
                    {!fs ? (
                        <button onClick={enterFs} data-testid="wb-fs-btn"
                                className="h-8 px-4 border text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5"
                                style={{ borderColor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                            <ArrowsOut size={12} weight="bold" /> Fullscreen
                        </button>
                    ) : (
                        <button onClick={exitFs} data-testid="wb-exit-fs-btn"
                                className="h-8 px-4 border text-xs uppercase tracking-[0.15em] inline-flex items-center gap-1.5"
                                style={{ borderColor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                            <X size={12} weight="bold" /> Zavřít
                        </button>
                    )}
                </div>
            </div>

            {/* Top row — 4 big KPI tiles */}
            <div className="grid grid-cols-4 gap-3 mb-3">
                <Tile label="Aktivní hovory" value={stats.active_calls} icon={Phone}
                      color="#22c55e" tid="wb-active" big />
                <Tile label="Dnes celkem" value={t.total} icon={Phone} tid="wb-today" />
                <Tile label="Přijaté" value={t.answered} icon={CheckCircle}
                      color="#22c55e" tid="wb-answered" />
                <Tile label="Zmeškané" value={t.missed} icon={PhoneSlash}
                      color={t.missed > 0 ? '#ef4444' : '#64748b'} tid="wb-missed" />
            </div>

            {/* Mid row — extension count + answer rate */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                <Tile label="Klapky online"
                      value={`${stats.extensions.online}/${stats.extensions.total}`}
                      icon={Users} color="#3b82f6" tid="wb-ext"
                      sub={`${stats.extensions.total - stats.extensions.online} offline`} />
                <AnswerRateBar rate={t.answer_rate} answered={t.answered} total={t.total} />
            </div>

            <div className="mb-3">
                <HourChart data={stats.by_hour} />
            </div>

            {/* Active calls table */}
            {stats.active_calls_list.length > 0 && (
                <div className="border overflow-hidden mb-3"
                     style={{ background: 'rgba(34,197,94,0.04)', borderColor: 'rgba(34,197,94,0.4)', borderRadius: 2 }}>
                    <div className="px-5 py-2.5 border-b text-[10px] font-bold uppercase tracking-[0.25em]"
                         style={{ borderColor: 'rgba(34,197,94,0.3)', color: '#22c55e' }}>
                        <Lightning size={11} weight="fill" className="inline mr-1" />
                        Aktivní hovory ({stats.active_calls_list.length}) · live AMI push
                    </div>
                    <table className="w-full" data-testid="wb-active-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider"
                                style={{ color: 'rgba(255,255,255,0.45)' }}>
                                <th className="px-5 py-1.5">Začátek</th>
                                <th className="px-5 py-1.5">Volající</th>
                                <th className="px-5 py-1.5">DID</th>
                                <th className="px-5 py-1.5">Cíl</th>
                                <th className="px-5 py-1.5">Trvání</th>
                            </tr>
                        </thead>
                        <tbody>
                        {stats.active_calls_list.map((c) => {
                            const elapsedSec = Math.max(0, Math.floor((Date.now() - new Date(c.started_at).getTime()) / 1000));
                            const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
                            const ss = String(elapsedSec % 60).padStart(2, '0');
                            return (
                                <tr key={c.id} className="border-t"
                                    style={{ borderColor: 'rgba(34,197,94,0.15)' }}>
                                    <td className="px-5 py-2.5 font-mono text-xs"
                                        style={{ color: 'rgba(255,255,255,0.65)' }}>
                                        {new Date(c.started_at).toLocaleTimeString('cs-CZ')}
                                    </td>
                                    <td className="px-5 py-2.5 font-mono text-sm">{c.caller_id || '—'}</td>
                                    <td className="px-5 py-2.5 font-mono text-sm">{c.did_number || '—'}</td>
                                    <td className="px-5 py-2.5 font-mono text-sm">{c.destination || '—'}</td>
                                    <td className="px-5 py-2.5 font-mono text-sm tabular-nums"
                                        style={{ color: '#22c55e' }}>{mm}:{ss}</td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Bottom row — recent + queues + AMI peers */}
            <div className="grid grid-cols-3 gap-3">
                <div className="border"
                     style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <div className="px-5 py-2 border-b text-[10px] font-bold uppercase tracking-[0.25em]"
                         style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
                        Posledních 10 hovorů
                    </div>
                    <table className="w-full text-xs" data-testid="wb-recent">
                        <tbody>
                        {stats.recent_calls.map((c) => (
                            <tr key={c.id} className="border-t"
                                style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                <td className="px-4 py-2 font-mono text-[11px]"
                                    style={{ color: 'rgba(255,255,255,0.55)' }}>
                                    {new Date(c.started_at).toLocaleTimeString('cs-CZ')}
                                </td>
                                <td className="px-4 py-2 font-mono">{c.caller_id || '—'}</td>
                                <td className="px-4 py-2 text-right">
                                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5"
                                          style={{
                                              background: c.status === 'answered'
                                                  ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                              color: c.status === 'answered' ? '#22c55e' : '#ef4444',
                                              borderRadius: 1,
                                          }}>
                                        {c.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {stats.recent_calls.length === 0 && (
                            <tr><td className="px-4 py-4 text-center text-xs"
                                    style={{ color: 'rgba(255,255,255,0.3)' }}>—</td></tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="border"
                     style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <div className="px-5 py-2 border-b text-[10px] font-bold uppercase tracking-[0.25em]"
                         style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
                        <Queue size={11} className="inline mr-1" /> Fronty
                    </div>
                    <table className="w-full text-xs" data-testid="wb-queues-table">
                        <tbody>
                        {stats.queues.map((q) => (
                            <tr key={q.id} className="border-t"
                                style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                <td className="px-4 py-2.5 font-bold">{q.name}</td>
                                <td className="px-4 py-2.5 font-mono text-[10px]"
                                    style={{ color: 'rgba(255,255,255,0.55)' }}>ext {q.extension_number}</td>
                                <td className="px-4 py-2.5 text-right font-mono">{q.members} agenti</td>
                            </tr>
                        ))}
                        {stats.queues.length === 0 && (
                            <tr><td className="px-4 py-4 text-center text-xs"
                                    style={{ color: 'rgba(255,255,255,0.3)' }}>Žádné fronty</td></tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="border"
                     style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                    <div className="px-5 py-2 border-b text-[10px] font-bold uppercase tracking-[0.25em]"
                         style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
                        <Broadcast size={11} className="inline mr-1" weight="duotone" /> Klapky · AMI live
                    </div>
                    <table className="w-full text-xs" data-testid="wb-peers">
                        <tbody>
                        {peerList.length > 0 ? peerList.map(([peer, info]) => {
                            const reachable = (info.status || '').toLowerCase().includes('reachable')
                                           || (info.status || '').toLowerCase() === 'registered';
                            const color = reachable ? '#22c55e' : '#ef4444';
                            return (
                                <tr key={peer} className="border-t"
                                    style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                    <td className="px-4 py-2.5 font-mono text-sm">{peer}</td>
                                    <td className="px-4 py-2.5 text-right">
                                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 font-mono"
                                              style={{ background: `${color}22`, color, borderRadius: 1 }}>
                                            ● {info.status || '?'}
                                        </span>
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr><td className="px-4 py-4 text-center text-xs"
                                    style={{ color: 'rgba(255,255,255,0.3)' }}>
                                Čekám na AMI události…
                            </td></tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Wallboard;
